import {
	ScrollableTabsNavigation,
	TabsNavigation,
	TabsNavigationItemData
} from "client/Components";
import {
	ReactElement,
	useMemo
} from "react";
import {
	SearchNodeModel,
	GlobalSearchScopeIdentifier,
	ScopeSearchNodeModel
} from "client/Api/TitleBar/GlobalSearch/globalSearch";
import { SearchNodeKind } from "client/Api/SearchSettings/searchSettings";
import {
	areSearchScopeIdentifiersEqual,
	useSearchResultsScopeTabs
} from "../Layout/TitleBar/GlobalSearch/useSearchResultsScopeTabs";
import styles from "./GroupView.module.less";
import { MultiScopeSearch } from "./SearchResults/MultiScopeSearch/MultiScopeSearch";
import { ScopeView } from "./ScopeView";
import classNames from "classnames";
import { convertToIdentifier } from "../Layout/TitleBar/GlobalSearch/useFetchGlobalSearchResults";

export type GroupViewProps = {
	searchTerm: VersionedValue<string>;
	selectedNode: SearchNodeModel;
	scopes: readonly ScopeSearchNodeModel[];
	mobileSearchSettingsElement: HTMLDivElement | null;
	isMobile: boolean;
	onScopeChange: (scope: GlobalSearchScopeIdentifier | null) => void;
};

export const GroupView = (props: GroupViewProps): ReactElement => {
	const {
		searchTerm,
		selectedNode,
		mobileSearchSettingsElement,
		isMobile,
		scopes,
		onScopeChange
	} = props;

	const scope = useMemo(
		(): ScopeSearchNodeModel | null => {
			if (selectedNode.kind === SearchNodeKind.Group) {
				return null;
			}

			return selectedNode;
		},
		[selectedNode]
	);

	const scopeTabs: readonly TabsNavigationItemData<GlobalSearchScopeIdentifier | null>[] =
		useSearchResultsScopeTabs(scopes);

	const renderResults = (): ReactElement | null => {
		if (!scope) {
			return (
				<MultiScopeSearch
					scopes={scopes}
					searchTerm={searchTerm}
					onViewMore={onScopeChange}
				/>
			);
		}

		return (
			<ScopeView
				scope={scope}
				mobileSearchSettingsElement={mobileSearchSettingsElement}
				searchTerm={searchTerm}
			/>
		);
	};

	const scopeIdentifier = useMemo(
		(): GlobalSearchScopeIdentifier | null => {
			return scope
				? convertToIdentifier(scope)
				: null;
		},
		[scope]
	);

	return (
		<>
			<div
				className={classNames(
					styles.scopeTabs,
					{
						[styles.scopeTabs_scrollable]: isMobile
					}
				)}
			>
				{isMobile
					? (
						<ScrollableTabsNavigation
							value={scopeIdentifier}
							onChange={onScopeChange}
							items={scopeTabs}
							areEqual={areSearchScopeIdentifiersEqual}
						/>
					)
					: (
						<TabsNavigation
							value={scopeIdentifier}
							onChange={onScopeChange}
							items={scopeTabs}
							areEqual={areSearchScopeIdentifiersEqual}
						/>
					)
				}
			</div>
			{renderResults()}
		</>
	);
};
GroupView.displayName = "GroupView";